import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  AdminSession,
  MediaItem,
  MessageItem,
  SessionSettingsPatch,
} from '@wallflower/shared';
import { api, qrUrl } from '../lib/api.ts';
import { accountApi, useAccount } from '../lib/accountApi.ts';
import { formatBytes } from '../lib/format.ts';
import { useSessionSocket } from '../lib/useSessionSocket.ts';
import { formatCountdown, useCountdown } from '../lib/useCountdown.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';
import SettingsPanel from '../components/SettingsPanel.tsx';
import MediaGrid from '../components/MediaGrid.tsx';
import MessageList from '../components/MessageList.tsx';
import CopyField from '../components/CopyField.tsx';
import PaymentDialog from '../components/PaymentDialog.tsx';

export default function Admin() {
  const { adminToken = '' } = useParams();
  useDocumentTitle('Control panel');
  const live = useSessionSocket({ adminToken });
  const [details, setDetails] = useState<AdminSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLinks, setShowLinks] = useState(false);
  const { account } = useAccount();
  const [ownedIds, setOwnedIds] = useState<string[]>([]);
  const [paying, setPaying] = useState(false);
  // Set when the payment dialog reports success; the webhook flipping the plan
  // over the socket is what makes the unpaid banner below actually go away.
  const [justPaid, setJustPaid] = useState(false);

  // Which walls are yours, so the header can offer a way back to the list. A
  // failure here is not worth surfacing: the panel works signed out by design.
  useEffect(() => {
    if (!account) {
      setOwnedIds([]);
      return;
    }
    let cancelled = false;
    accountApi
      .sessions()
      .then((result) => {
        if (!cancelled) setOwnedIds(result.sessions.map((s) => s.id));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [account]);

  // The socket carries settings and media; this fetch adds the admin-only extras
  // (the shareable URLs and disk usage) and re-runs whenever the link recovers.
  useEffect(() => {
    if (live.status !== 'live') return;
    let cancelled = false;
    api
      .adminSession(adminToken)
      .then((result) => {
        if (!cancelled) setDetails(result.session);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load this event.');
      });
    return () => {
      cancelled = true;
    };
  }, [adminToken, live.status]);

  const settings = live.session?.settings ?? details?.settings ?? null;
  const session = live.session ?? details;
  const isDemo = session?.plan === 'demo';
  const isUnpaid = session?.plan === 'unpaid';
  // Signed in and this wall is in your list: only then is a link back useful.
  const ownsThisWall = Boolean(account) && ownedIds.includes(session?.id ?? '');
  const remainingMs = useCountdown(isDemo ? session?.expiresAt : null);

  const patchSettings = useCallback(
    (patch: SessionSettingsPatch) => {
      // The socket echo is the source of truth; this keeps the control snappy.
      api.updateSettings(adminToken, patch).catch(() => setError('That change did not save.'));
    },
    [adminToken],
  );

  const toggleHidden = useCallback(
    (item: MediaItem) => {
      api
        .setHidden(adminToken, item.id, !item.hidden)
        .catch(() => setError('Could not change that item.'));
    },
    [adminToken],
  );

  const removeItem = useCallback(
    (item: MediaItem) => {
      api.deleteMedia(adminToken, item.id).catch(() => setError('Could not delete that item.'));
    },
    [adminToken],
  );

  const postAnnouncement = useCallback(
    async (body: string, authorName: string | null) => {
      try {
        await api.postHostMessage(adminToken, body, authorName);
      } catch {
        setError('Could not post that announcement.');
      }
    },
    [adminToken],
  );

  const toggleMessageHidden = useCallback(
    (message: MessageItem) => {
      api
        .setMessageHidden(adminToken, message.id, !message.hidden)
        .catch(() => setError('Could not change that message.'));
    },
    [adminToken],
  );

  const removeMessage = useCallback(
    (message: MessageItem) => {
      api
        .deleteMessage(adminToken, message.id)
        .catch(() => setError('Could not delete that message.'));
    },
    [adminToken],
  );

  if (live.status === 'gone') {
    return (
      <main className="grid flex-1 place-items-center px-6 text-center">
        <div>
          <h1 className="font-display text-3xl font-semibold">This event is gone</h1>
          <p className="mt-3 text-muted">
            It was deleted, or it passed its expiry date and was cleaned up.
          </p>
        </div>
      </main>
    );
  }

  if (!settings) {
    return (
      <main className="grid flex-1 place-items-center px-6">
        <p className="text-muted">{error ?? 'Opening your control panel…'}</p>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-16 sm:px-6">
      <header className="sticky top-0 z-30 -mx-4 mb-6 border-b border-edge bg-dusk/90 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="min-w-0">
            {/*
              Only when this wall is one of yours. The control panel is a
              capability link first - it has to keep working for a co-host who has
              no account and never signs in.
            */}
            {ownsThisWall ? (
              <Link to="/dashboard" className="eyebrow hover:text-petal">
                ← Your events
              </Link>
            ) : (
              <p className="eyebrow">Control panel</p>
            )}
            <h1 className="font-display truncate text-xl font-semibold">
              {live.session?.name ?? details?.name}
            </h1>
          </div>

          <span
            className="ml-auto flex shrink-0 items-center gap-2 text-xs text-muted"
            title={
              live.degraded
                ? 'The live connection is not holding, so this is refreshing over plain HTTP instead'
                : live.status === 'live'
                  ? 'Connected'
                  : 'Trying to reconnect'
            }
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{
                backgroundColor:
                  live.status === 'live' && !live.degraded
                    ? 'var(--color-stem)'
                    : 'var(--color-sulphur)',
              }}
            />
            {/* "Connected", not "Live": this pill is about the socket, and the
                wall being live is a different thing said a few lines below. */}
            {live.degraded ? 'Refreshing' : live.status === 'live' ? 'Connected' : 'Reconnecting'}
          </span>

          <button className="btn btn-ghost shrink-0 px-3 py-2 text-sm" onClick={() => setShowLinks((v) => !v)}>
            Links
          </button>
        </div>

        {showLinks && details ? (
          <div className="grid gap-3 py-4 sm:grid-cols-2">
            <CopyField label="Open on the TV" value={details.slideshowUrl} />
            <CopyField label="Guest upload page" value={details.uploadUrl} />
            <div className="panel flex items-center gap-4 p-4 sm:col-span-2">
              <img
                src={qrUrl(details.id, 'sm')}
                alt="QR code guests can scan"
                className="h-24 w-24 rounded bg-white p-1"
              />
              <div className="text-sm text-muted">
                <p className="text-chalk">The same code that shows on the TV.</p>
                <p className="mt-1">
                  Using {formatBytes(details.storageBytes)} · originals kept until{' '}
                  {new Date(details.expiresAt).toLocaleDateString()}
                </p>
                <a className="mt-2 inline-block text-petal hover:underline" href={`/api/admin/${adminToken}/export.zip`}>
                  Download every original
                </a>
              </div>
            </div>
          </div>
        ) : null}
      </header>

      {justPaid ? (
        <div className="panel mb-6 flex items-center justify-between gap-4 p-4">
          <p className="text-sm">
            <span className="text-chalk">Payment received.</span>{' '}
            <span className="text-muted">
              The wall goes live the moment Stripe confirms it, usually a few seconds.
            </span>
          </p>
          <button
            type="button"
            className="shrink-0 text-sm text-petal hover:underline"
            onClick={() => setJustPaid(false)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* An unpaid wall has real limits of zero: guests are turned away until a
          checkout completes, so paying is the first thing the panel offers. */}
      {isUnpaid ? (
        <div className="panel mb-6 flex flex-wrap items-center justify-between gap-4 p-4">
          <p className="text-sm">
            <span className="text-chalk">This wall is not paid for yet.</span>{' '}
            <span className="text-muted">
              The links work, but guests cannot add anything until it is.
            </span>
          </p>
          <button
            type="button"
            className="btn btn-primary shrink-0"
            onClick={() => setPaying(true)}
          >
            Pay to go live
          </button>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="panel mb-6 p-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {isDemo && session ? (
        <div className="panel mb-6 flex flex-wrap items-baseline gap-x-3 gap-y-1 p-4 text-sm">
          <span className="eyebrow">Demo wall</span>
          <span className="font-display text-lg text-sulphur">
            {remainingMs !== null ? `${formatCountdown(remainingMs)} left` : ''}
          </span>
          <span className="text-muted">
            {session.usage.media} of {session.limits.media} photos ·{' '}
            {session.usage.messages} of {session.limits.messages} messages · video off. Everything
            here is deleted when the clock runs out.
          </span>
        </div>
      ) : null}

      {/* Moderation comes first on a phone: if something needs pulling off the
          screen, it needs pulling off now. */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="grid gap-6">
          <MediaGrid media={live.media} onToggleHidden={toggleHidden} onDelete={removeItem} />
          <MessageList
            messages={live.messages}
            allowMessages={settings.allowMessages}
            onPost={postAnnouncement}
            onToggleHidden={toggleMessageHidden}
            onDelete={removeMessage}
          />
        </div>
        <SettingsPanel settings={settings} onChange={patchSettings} videoLocked={isDemo} />
      </div>

      {paying ? (
        <PaymentDialog
          wallName={session?.name ?? 'this wall'}
          start={() => api.adminCheckout(adminToken)}
          onPaid={() => {
            setPaying(false);
            setJustPaid(true);
          }}
          onClose={() => setPaying(false)}
        />
      ) : null}
    </main>
  );
}
